// =================================
// 静的チェック（AI作成が疑われる表現の検知）
// =================================

import type { BaseSuggestion } from "@/vue/stores/modules/anytime_consultation_corrections";

export type StaticCheckRuleType =
  | "asterisk"
  | "doubleQuote"
  | "brackets"
  | "dash"
  | "colonSpace";

export const STATIC_CHECK_RULE_LABELS: Record<StaticCheckRuleType, string> = {
  asterisk: "AI作成が疑われる表現",
  doubleQuote: "AI作成が疑われる表現",
  brackets: "AI作成が疑われる表現",
  dash: "AI作成が疑われる表現",
  colonSpace: "AI作成が疑われる表現",
};

export const STATIC_CHECK_RULE_REASONS: Record<StaticCheckRuleType, string> = {
  asterisk: "アスタリスク * による強調",
  doubleQuote: "ダブルクオテーション \" の多用",
  brackets: "「」による切り出しの多用",
  dash: "ダッシュの利用",
  colonSpace: "コロンの利用",
};

export interface StaticCheckFinding extends Omit<BaseSuggestion, "correctionType"> {
  correctionType: "staticCheck";
  quote: string;
  reason: string;
  ruleType: StaticCheckRuleType;
}

export interface RunStaticCheckRulesOptions {
  everTriggeredStaticCheckRules?: {
    doubleQuote: boolean;
    brackets: boolean;
  };
}

// IDはruleType+occurrence+quoteのハッシュ（自然数）を使い、
// AI添削のSuggestion IDと衝突せず、回答文を編集しても同じ指摘には同じIDが振られるようにする
// 現状の実装では同一 ruleType の中で前の出現箇所が追加/削除されると occurrence がズレて ID が変わってしまうが、解決手段が浮かばないので、しかたなく許容
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return hash;
};

const stableId = (ruleType: string, quote: string, occurrence: number): number => {
  const raw = hashString(`${ruleType}:${occurrence}:${quote}`);
  // 0を避けつつ必ず自然数にする
  return raw >= 0 ? raw + 1 : -raw;
};

const buildFinding = (
  quote: string,
  ruleType: StaticCheckRuleType,
  occurrence: number,
  replacement: string | null = null,
): StaticCheckFinding => ({
  id: stableId(ruleType, quote, occurrence),
  correctionType: "staticCheck",
  quote,
  reason: STATIC_CHECK_RULE_REASONS[ruleType],
  ruleType,
  replacement,
  consultationExtractedQuestionId: null,
  accepted: false,
  ignored: false,
});

// --------------------------------------------------
// ルール1: アスタリスク強調
// 1行にアスタリスク * が1個以上あれば指摘
// 最初の * から最後の * までを指摘箇所として抽出
// --------------------------------------------------
const checkAsterisk = (content: string): StaticCheckFinding[] => {
  const findings: StaticCheckFinding[] = [];
  const lines = content.split("\n");
  let occurrence = 0;
  for (const line of lines) {
    const firstIdx = line.indexOf("*");
    if (firstIdx === -1) continue;
    const lastIdx = line.lastIndexOf("*");
    const quote = line.slice(firstIdx, lastIdx + 1);
    if (quote.length === 0) continue;
    const replacement = quote.replace(/\*/g, "");
    findings.push(buildFinding(quote, "asterisk", occurrence++, replacement));
  }
  return findings;
};

// --------------------------------------------------
// ルール2: ダブルクオテーションの多用
// 回答文全体で "..." が2組以上の場合、各 "..." を指摘
// --------------------------------------------------
const checkDoubleQuote = (
  content: string,
  everTriggered: boolean,
): StaticCheckFinding[] => {
  const regex = /\u201c[^\u201d\n]+\u201d|"[^"\n]+"/g;
  const matches = [...content.matchAll(regex)];
  const threshold = everTriggered ? 1 : 2;
  if (matches.length < threshold) return [];
  return matches.map((m, i) => {
    const quote = m[0];
    return buildFinding(quote, "doubleQuote", i);
  });
};

// --------------------------------------------------
// ルール3: 「」で言葉を切り出しすぎ
// 回答文全体で「」が2組以上の場合、改行を含まない「...」を指摘
// --------------------------------------------------
const checkBrackets = (
  content: string,
  everTriggered: boolean,
): StaticCheckFinding[] => {
  const regex = /「[^」\n]+」/g;
  const matches = [...content.matchAll(regex)];
  const threshold = everTriggered ? 1 : 2;
  if (matches.length < threshold) return [];
  return matches.map((m, i) => {
    const quote = m[0];
    return buildFinding(quote, "brackets", i);
  });
};

// --------------------------------------------------
// ルール4: ダッシュによる言い換え
// 以下のいずれかのダッシュが使われていれば指摘する
// —（U+2014 emダッシュ）
// ―（U+2015 水平線）
// --------------------------------------------------
const checkDash = (content: string): StaticCheckFinding[] => {
  const findings: StaticCheckFinding[] = [];
  const emDash = "—"; // U+2014
  const horizontalBar = "―"; // U+2015
  let occurrence = 0;
  
  // 各ダッシュの出現位置を収集
  const positions: Array<{ char: string; idx: number }> = [];
  
  let idx = content.indexOf(emDash);
  while (idx !== -1) {
    positions.push({ char: emDash, idx });
    idx = content.indexOf(emDash, idx + 1);
  }
  
  idx = content.indexOf(horizontalBar);
  while (idx !== -1) {
    positions.push({ char: horizontalBar, idx });
    idx = content.indexOf(horizontalBar, idx + 1);
  }
  
  // 出現位置でソート
  positions.sort((a, b) => a.idx - b.idx);
  
  // ソート済み順序で指摘を生成
  for (const { char } of positions) {
    findings.push(buildFinding(char, "dash", occurrence++));
  }
  
  return findings;
};

// --------------------------------------------------
// ルール5: コロンの後に半角スペース
// 「注意点: 」のように、コロン(: or ：)の後に半角スペースがある行の
// 行頭からコロン+スペースまでを指摘箇所として抽出
// --------------------------------------------------
const checkColonSpace = (content: string): StaticCheckFinding[] => {
  const findings: StaticCheckFinding[] = [];
  const lines = content.split("\n");
  let occurrence = 0;
  for (const line of lines) {
    const match = line.match(/^(.*?[：:] )/);
    if (match) {
      findings.push(buildFinding(match[1], "colonSpace", occurrence++));
    }
  }
  return findings;
};

// --------------------------------------------------
// 全ルールを実行し、結果をまとめて返す
// --------------------------------------------------
export const runStaticCheckRules = (
  answerContent: string,
  options: RunStaticCheckRulesOptions = {},
): StaticCheckFinding[] => {
  if (!answerContent) return [];
  const everTriggeredStaticCheckRules = options.everTriggeredStaticCheckRules ?? {
    doubleQuote: false,
    brackets: false,
  };
  return [
    ...checkAsterisk(answerContent),
    ...checkDoubleQuote(answerContent, everTriggeredStaticCheckRules.doubleQuote),
    ...checkBrackets(answerContent, everTriggeredStaticCheckRules.brackets),
    ...checkDash(answerContent),
    ...checkColonSpace(answerContent),
  ];
};
